import {MaintainedSource} from './MaintainedSource.ts';
import type {YomiriManga,YomiriChapter} from '../../types/extension.ts';
interface OlympusEntry {
    name: string;
    slug: string;
    cover?: string;
    summary?: string;
}
interface OlympusList<T> {
    data: T[];
    current_page?: number;
    last_page?: number;
    meta?: {
        total: number;
    };
}
export class OlympusSource extends MaintainedSource {
    readonly id = 'olympusscanlation';
    readonly name = 'Olympus Scanlation';
    readonly website = 'https://olympusxyz.com';
    readonly supportsLatestUpdates = true;
    async getLatest() {
        const response = await this.json<{ data: { new_chapters: (OlympusEntry & { type?: string; last_chapters?: { published_at?: string }[] })[] } }>(`${this.website}/api/homepage`);
        const entries = response.data.new_chapters.filter(entry => entry.type === 'comic');
        const timestamp = (entry: typeof entries[number]) => Math.max(0, ...(entry.last_chapters ?? []).map(chapter => Date.parse(chapter.published_at ?? '') || 0));
        entries.sort((a, b) => timestamp(b) - timestamp(a));
        return { mangas: [...new Map(entries.map(entry => [entry.slug, this.map(entry)])).values()], hasNextPage: false };
    }
    private all?: Promise<OlympusEntry[]>;
    private map(e: OlympusEntry) { return { ...this.manga(`${this.website}/series/comic-${e.slug}`, e.name, e.cover), description: e.summary }; }
    async getPopular(page = 1) { const r = await this.json<OlympusList<OlympusEntry>>(`${this.website}/api/rankings?page=${page}&period=total_ranking`); return { mangas: r.data.map(e => this.map(e)), hasNextPage: page < (r.last_page || 1) }; }
    async search(q: string, page = 1) { this.all ??= this.json<OlympusList<OlympusEntry>>(`${this.website}/api/series/list`).then(r => r.data).catch(e => { this.all = undefined; throw e; }); const results = (await this.all).filter(e => e.name.toLocaleLowerCase().includes(q.toLocaleLowerCase())); return { mangas: results.slice((page - 1) * 20, page * 20).map(e => this.map(e)), hasNextPage: page * 20 < results.length }; }
    private slug(id: string) { return new URL(id).pathname.split('/').pop()!.replace(/^comic-/, ''); }
    async getManga(id: string) { const r = await this.json<{
        data: OlympusEntry;
    }>(`${this.website}/api/series/${this.slug(id)}?type=comic`); return { ...this.map(r.data), id, url: id }; }
    async getChapters(id: string) { const slug = this.slug(id); const results: YomiriChapter[] = []; for (let page = 1; page <= 100; page++) {
        const r = await this.json<OlympusList<{
            id: number;
            name: string;
        }>>(`${this.website}/api/series/${slug}/chapters?page=${page}&direction=desc&type=comic`);
        results.push(...r.data.map(c => this.chapter(`${this.website}/series/comic-${slug}/capitulo/${encodeURIComponent(c.name)}`, id, `Capítulo ${c.name}`)));
        if (!r.data.length || (r.last_page !== undefined ? page >= r.last_page : r.meta?.total !== undefined ? results.length >= r.meta.total : r.data.length < 20))
            break;
    } return results; }
    async getPages(id: string) {
        let url = new URL(id);
        if (url.pathname.startsWith('/capitulo/')) {
            const response = await this.http.text({ sourceId: this.id, url: url.href, method: 'GET', responseType: 'text', credentials: 'omit', cache: { ttlMs: 300000, scope: 'public' } });
            url = new URL(response.finalUrl);
        }
        const parts = url.pathname.split('/').filter(Boolean);
        if (parts[0] !== 'series' || parts[2] !== 'capitulo' || !parts[3]) throw new Error('Olympus: no se pudo resolver la dirección del capítulo.');
        const slug = parts[1].replace(/^comic-/, '');
        const r = await this.json<{
        chapter: {
            pages: string[];
        };
    }>(`${this.website}/api/capitulo/${slug}/${parts[3]}?type=comic`); return this.pages(r.chapter.pages, url.href); }
}
